import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import distributorService from '../../../../services/DistributorService';
import { showToast } from '../../../../utils/toast';

const DistributorOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const fileInputRef = useRef(null);

  // Fetch Orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchQuery,
        status: statusFilter,
        date_from: dateFrom,
        date_to: dateTo
      };

      const response = await distributorService.getOrders(params);
      
      if (response.success) {
        setOrders(response.data.results || []);
        setTotalCount(response.data.count || 0);
      } else {
        showToast.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchQuery, statusFilter, dateFrom, dateTo]);

  // Debounced effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrders();
    }, searchQuery ? 300 : 0); // Debounce search queries

    return () => clearTimeout(timer);
  }, [fetchOrders]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (filterType, value) => {
    // Batch state updates to prevent multiple API calls
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'dateFrom') {
      setDateFrom(value);
    } else if (filterType === 'dateTo') {
      setDateTo(value);
    }
    
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };


  const exportToExcel = async () => {
    setExporting(true);
    try {
      const exportParams = {
        date_from: dateFrom,
        date_to: dateTo,
        status: statusFilter,
        search: searchQuery
      };

      const response = await distributorService.exportOrders(exportParams);
      
      if (response.success) {
        // Create blob and download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName || `orders_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        showToast.success('Orders exported successfully');
      } else {
        showToast.error(response.error || 'Failed to export orders');
      }
    } catch (error) {
      console.error('Error exporting orders:', error);
      showToast.error('Error exporting orders');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await distributorService.importOrders(file);
      
      if (response.success) {
        showToast.success(response.message || 'Orders imported successfully');
        fetchOrders();
      } else {
        showToast.error(response.error || 'Failed to import orders');
      }
    } catch (error) {
      console.error('Error importing orders:', error);
      showToast.error('Error importing orders');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getStatusBadge = (status, statusDesc) => {
    const variant = status === 'Completed' ? 'success' : 
                   status === 'Pending' ? 'warning' : 
                   status === 'Cancelled' ? 'danger' : 'secondary';
    return <Badge bg={variant}>{statusDesc || status}</Badge>;
  };

  const getReconcileBadge = (reconcile) => {
    return reconcile ? 
      <Badge bg="success">Added to Outstanding</Badge> : 
      <Badge bg="warning">Pending</Badge>;
  };

  const getPushIndBadge = (pushInd) => {
    return pushInd === 'A' ? 
      <Badge bg="info">Auto</Badge> : 
      <Badge bg="secondary">Distributor</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-shopping-cart me-2"></i>
          Topup Orders
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel} disabled={exporting}>
            {exporting ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-file-excel me-2"></i>}
            Export Excel
          </Button>
          <Button 
            variant="info" 
            onClick={() => fileInputRef.current.click()}
            disabled={importing}
          >
            {importing ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-upload me-2"></i>}
            Import Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImport}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            placeholder="From Date"
            value={dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            placeholder="To Date"
            value={dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
        </Col>
        <Col md={3}>
          {/* Space for future filters */}
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Customer Details</th>
                <th>Order Amount</th>
                <th>Granted Value</th>
                <th>FOS ID</th>
                <th>Status</th>
                <th>Push Type</th>
                <th>Outstanding Status</th>
                <th>Creation Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.OrderNum}</strong></td>
                  <td>
                    <div>
                      <div>{order.CustName}</div>
                      <small className="text-muted">ID: {order.CustomerNum}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{formatCurrency(order.OrderAmt)}</div>
                      <small className="text-muted">Amount</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{formatCurrency(order.GrantedVal)}</div>
                      <small className="text-muted">Granted</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{order.FosId}</div>
                      <small className="text-muted">FOS</small>
                    </div>
                  </td>
                  <td>{getStatusBadge(order.Status, order.StatusDesc)}</td>
                  <td>{getPushIndBadge(order.PushInd)}</td>
                  <td>{getReconcileBadge(order.reconcile)}</td>
                  <td>
                    <small className="text-muted">
                      {formatDate(order.CreationDate)}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {orders.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No orders found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              const newRowsPerPage = Number(e.target.value);
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1); // Reset to first page when changing page size
            }}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>


    </Container>
  );
};

export default DistributorOrders;